import { useEffect, useRef, useState } from 'react'
import { Card } from '@/components/ui/Card'
import { Text } from '@/components/ui/Text'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { LoadingState } from '@/components/ui/StateViews'

export type GuestAuthPanelState =
  | { tag: 'start' }
  | { tag: 'sending' }
  | { tag: 'checkEmail'; onSubmit: (code: string) => void; onResend: () => void; error?: 'codeRejected' }
  | { tag: 'verifying' }
  | { tag: 'invalid' }
  | { tag: 'wait' }
  | { tag: 'unavailable'; onRetry: () => void }
  | { tag: 'signedOut' }
  | { tag: 'logoutFailed'; onRetry: () => void }

export interface GuestAuthPanelProps {
  state: GuestAuthPanelState
}

const CODE_LENGTH = 6
const CODE_PATTERN = /^\d{6}$/

/** Strips every non-digit so the field cannot submit a non-digit value, regardless of entry path. */
function digitsOnly(value: string): string {
  return value.replace(/\D/g, '').slice(0, CODE_LENGTH)
}

function headingFor(state: GuestAuthPanelState): string {
  switch (state.tag) {
    case 'start':
      return 'Opening your link'
    case 'sending':
      return 'Checking your access'
    case 'checkEmail':
      return 'Enter the code we sent'
    case 'verifying':
      return 'Verifying your code'
    case 'invalid':
      return 'Link no longer valid'
    case 'wait':
      return 'Please wait'
    case 'unavailable':
      return 'Temporarily unavailable'
    case 'signedOut':
      return 'You have signed out'
    case 'logoutFailed':
      return "Couldn't sign you out"
  }
}

/**
 * The FR-DF3 refusal text: used for BOTH an invalid token and an expired session — one state,
 * one copy, no prop distinguishes them (the discriminated union has no payload on `'invalid'`).
 */
const INVALID_COPY = 'This link is no longer valid. Open the link in your email again for a new code.'
const CODE_REJECTED_COPY = 'That code did not work. Check it, or send a new code.'
const SIGNED_OUT_COPY = 'You have signed out. To view again, open the link in your email.'
const WAIT_COPY = "You've reached the limit for now. Please wait before trying again."
const UNAVAILABLE_COPY = 'This is temporarily unavailable. Please try again in a moment.'
// ENT-566 decision (orchestrator/docs/prd/ent-566-task-first-f1.md:661; T08 amendment note):
// the guest is told to retry instead of a false "signed out" while the server session may still
// be valid. The token is already erased locally either way.
const LOGOUT_FAILED_COPY = "We couldn't confirm you're signed out. Try again."

export function GuestAuthPanel({ state }: GuestAuthPanelProps) {
  const headingRef = useRef<HTMLElement>(null)

  // Heading receives focus on every state change (AC-P0d.F10) — the one signal a screen-reader
  // user gets that the shell moved, since there is no route change to announce it.
  useEffect(() => {
    headingRef.current?.focus()
  }, [state.tag])

  // `aria-live` sits on this wrapper, not on the heading alone — the status COPY (invalid/wait/
  // unavailable/signedOut text) is a sibling of the heading, not a descendant, so a live region on
  // the heading only would announce the heading's own text change and leave the copy unannounced.
  const heading = (
    <Text as="h1" variant="title-md" tabIndex={-1} ref={headingRef}>
      {headingFor(state)}
    </Text>
  )

  return (
    <Card className="p-6 flex flex-col gap-4 max-w-sm w-full">
      <div aria-live="polite" aria-atomic="true" className="flex flex-col gap-4">
        {heading}
        {state.tag === 'start' && <LoadingState label="One moment…" />}
        {state.tag === 'sending' && <LoadingState label="One moment…" />}
        {state.tag === 'verifying' && <LoadingState label="One moment…" />}
        {state.tag === 'invalid' && (
          <Text variant="body-sm" color="muted">
            {INVALID_COPY}
          </Text>
        )}
        {state.tag === 'wait' && (
          <Text variant="body-sm" color="muted">
            {WAIT_COPY}
          </Text>
        )}
        {state.tag === 'unavailable' && (
          <>
            <Text variant="body-sm" color="muted">
              {UNAVAILABLE_COPY}
            </Text>
            <Button variant="outline" onClick={state.onRetry}>
              Try again
            </Button>
          </>
        )}
        {state.tag === 'signedOut' && (
          <Text variant="body-sm" color="muted">
            {SIGNED_OUT_COPY}
          </Text>
        )}
        {state.tag === 'logoutFailed' && (
          <>
            <Text variant="body-sm" color="muted">
              {LOGOUT_FAILED_COPY}
            </Text>
            <Button variant="outline" onClick={state.onRetry}>
              Try again
            </Button>
          </>
        )}
      </div>
      {state.tag === 'checkEmail' && (
        // Keyed on the error so a NEW rejection remounts the form (fresh `code` state, cleared
        // field) while an unrelated re-render with the same error never wipes mid-typed input.
        <CheckEmailForm key={state.error ?? 'none'} state={state} />
      )}
    </Card>
  )
}

interface CheckEmailFormProps {
  state: Extract<GuestAuthPanelState, { tag: 'checkEmail' }>
}

function CheckEmailForm({ state }: CheckEmailFormProps) {
  const [code, setCode] = useState('')
  const errorId = 'guest-auth-code-error'
  const canSubmit = CODE_PATTERN.test(code)

  function submitIfValid(value: string) {
    if (CODE_PATTERN.test(value)) {
      state.onSubmit(value)
    }
  }

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault()
        submitIfValid(code)
      }}
    >
      <Input
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={CODE_LENGTH}
        pattern="[0-9]{6}"
        aria-label="6-digit code"
        value={code}
        aria-invalid={state.error === 'codeRejected'}
        aria-describedby={state.error === 'codeRejected' ? errorId : undefined}
        onChange={(e) => setCode(digitsOnly(e.target.value))}
        onPaste={(e) => {
          const pasted = digitsOnly(e.clipboardData.getData('text'))
          if (CODE_PATTERN.test(pasted)) {
            e.preventDefault()
            setCode(pasted)
            // A pasted full code submits once, immediately — the user already typed nothing.
            submitIfValid(pasted)
          }
        }}
      />
      {state.error === 'codeRejected' && (
        <Text id={errorId} variant="body-sm" color="danger" role="alert">
          {CODE_REJECTED_COPY}
        </Text>
      )}
      <Button type="submit" disabled={!canSubmit}>
        Continue
      </Button>
      <Button type="button" variant="ghost" onClick={state.onResend}>
        Send a new code
      </Button>
    </form>
  )
}
